import { Box, Collapse, Grid, IconButton, Typography } from "@mui/material";
import ExpandableSearchBox from "./ExpandableSearch";

import { Close } from "@mui/icons-material";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NewJobForm from "../../../modules/HireV3/JobRequestTable/JobRequestForm";
import AddNewTaskModal from "../../../modules/Tasks/AddNewTaskModal";
import CommonFilterMenu, {
  MenuOptions,
  Option,
} from "../CommonFilters/CommonFilterMenu";
import FilterView from "./FilterView";
import SplitButton, { SplitButtonOption } from "./SplitButton";

type BaseProps = {
  onSearch?: (value: string) => void;
  secondaryActions?: React.ReactNode;
  defaultSelectedId?: string;
};

type WithBreadcrumbs = {
  breadCrumbs: React.ReactNode;
  title: string;
};

type WithoutBreadcrumbs = {
  breadCrumbs?: undefined;
  title?: undefined;
};

type WithOptions = {
  options: Array<MenuOptions>; // or whatever type your options are
  filters: Option; // or the actual filter type
  setFilters: (filters: Option) => void;
};

type WithoutOptions = {
  options?: undefined;
  filters?: undefined;
  setFilters?: undefined;
};

type Props = BaseProps &
  (WithOptions | WithoutOptions) &
  (WithBreadcrumbs | WithoutBreadcrumbs);

const actions: SplitButtonOption[] = [
  {
    label: "New job request",
    id: "job-request",
  },
  {
    label: "New Task",
    id: "task",
  },
];

const Header = ({
  filters,
  onSearch,
  breadCrumbs,
  title,
  options,
  setFilters,
  secondaryActions,
  defaultSelectedId = "job-request",
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const navigate = useNavigate();
  const showMenu = Boolean(anchorEl);
  const closeMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const [active, setActive] = useState<string>("");

  const handleSelect = (id: string) => {
    if (id === "job-request") {
      navigate("/job-request/new");
      return;
    }
    setActive(id);
    // do stuff based on id
  };

  const handleFilterClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const filteredFilters = useMemo(() => {
    return Object.entries(filters || {}).filter((el) => el[1] !== "");
  }, [filters]);

  const clearFilter = useCallback(
    (el) => () => {
      setFilters?.({ [el]: "" });
    },
    [setFilters],
  );

  const closeView = useCallback(() => {
    setActive("");
  }, []);

  return (
    <Grid container direction={"column"}>
      <Grid
        container
        item
        justifyContent={"space-between"}
        alignItems={"center"}
        wrap="nowrap"
      >
        <Grid container item gap={2} xs={7} alignItems={"center"}>
          <SplitButton
            options={actions}
            defaultSelectedId={defaultSelectedId}
            onSelect={handleSelect}
          />

          {typeof breadCrumbs === "undefined" && (
            <>
              {options && options.length > 0 && (
                <>
                  <IconButton onClick={handleFilterClick}>
                    <img src="/images/settings.svg" alt="filter" />
                  </IconButton>
                  <CommonFilterMenu
                    anchorEl={anchorEl}
                    handleClose={closeMenu}
                    open={showMenu}
                    options={options}
                    changeOption={setFilters}
                    filters={filters}
                  ></CommonFilterMenu>
                </>
              )}
              {onSearch && <ExpandableSearchBox onSearch={onSearch} />}
            </>
          )}
        </Grid>
        <Grid container item justifyContent={"flex-end"} xs={5}>
          {secondaryActions}
        </Grid>
      </Grid>
      {typeof breadCrumbs !== "undefined" && (
        <Grid container item xs={12} direction={"column"}>
          <Grid item> {breadCrumbs}</Grid>
          <Grid
            container
            item
            justifyContent={"space-between"}
            alignItems={"center"}
            wrap="nowrap"
          >
            <Grid container item>
              <Typography
                fontSize={"32px"}
                fontWeight={"700"}
                color={"#3F4871"}
              >
                {title}
              </Typography>
            </Grid>
            <Grid
              container
              item
              wrap="nowrap"
              justifyContent={"flex-end"}
              gap={1}
            >
              {onSearch && <ExpandableSearchBox onSearch={onSearch} />}
              {options && options.length > 0 && (
                <>
                  <IconButton onClick={handleFilterClick}>
                    <img src="/images/settings.svg" alt="filter" />
                  </IconButton>
                  <CommonFilterMenu
                    anchorEl={anchorEl}
                    handleClose={closeMenu}
                    open={showMenu}
                    options={options}
                    changeOption={setFilters}
                    filters={filters}
                  ></CommonFilterMenu>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid container item xs={12} sx={{ display: "flex" }}>
        <Collapse in={filteredFilters.length > 0}>
          <Grid container flexWrap={"nowrap"} gap={2} my={1}>
            {filteredFilters.map((el) => (
              <FilterView
                key={el[0]}
                el={el}
                onClick={clearFilter(el[0])}
                options={options || []}
              />
            ))}
          </Grid>
        </Collapse>
      </Grid>
      <Collapse
        sx={{
          width: "100%",
        }}
        in={active === "job-request"}
        easing={{ enter: "ease-in" }}
        unmountOnExit
      >
        <Grid borderLeft={"2px solid #A16AE8"}>
          <Box
            sx={{
              backgroundColor: "#fff",
              display: "flex",
              boxShadow: "0px 4px 8px 0px #0000000D",
              px: 2,
              py: 2,
              mt: 2,
              justifyContent: "space-between",
            }}
          >
            <Typography fontSize={"24px"} lineHeight={"32px"} fontWeight={700}>
              New request
            </Typography>
            <IconButton
              aria-label="close"
              onClick={closeView}
              size="small"
              sx={{
                color: "black",
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <NewJobForm
            setShowNewJobRequest={(flag) => {
              if (!flag) {
                setActive("");
              }
            }}
          />
        </Grid>
      </Collapse>

      <Collapse
        in={active === "task"}
        sx={{
          boxShadow: "0px 4px 8px 0px #0000000D",
        }}
        easing={{ enter: "ease-in" }}
        unmountOnExit
      >
        <Grid
          py={1}
          px={2}
          bgcolor={"#fff"}
          mt={2}
          borderLeft={"2px solid #A16AE8"}
        >
          <Typography fontSize={"24px"} lineHeight={"32px"} fontWeight={700}>
            Add new task
          </Typography>
          <AddNewTaskModal closeModal={closeView}></AddNewTaskModal>
        </Grid>
      </Collapse>
    </Grid>
  );
};

export default Header;
